import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { login, clearError } from '../../store/slices/authSlice';
import { validateEmail } from '../../utils/validation';
import { TeslaButton, TeslaInput, EmailIcon, LockIcon, EyeIcon, EyeOffIcon } from '../common';
import { toAuthErrorMessage } from '../../utils/authError';
import TurnstileWidget from './TurnstileWidget';

const LoginForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { status, error } = useAppSelector((state) => state.auth);
  const errorMessage = toAuthErrorMessage(error);

  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [touched, setTouched] = useState({ email: false, password: false });
  const [showPassword, setShowPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReset, setTurnstileReset] = useState(0);
  const submittingRef = React.useRef(false);

  // Disable auto-redirect for now to fix login page issues
  /*
  useEffect(() => {
    if (isAuthenticated && user) {
      // Navigate to the correct dashboard based on user type
      // Use userType if present, otherwise fall back to role for backwards compatibility
      const userType = user.userType || user.role;
      const dashboardRoute = userType === 'stylist' ? '/stylist/dashboard' : '/client/dashboard';
      navigate(dashboardRoute);
    }
  }, [isAuthenticated, user, navigate]);
  */

  const getEmailError = () => {
    if (!credentials.email.trim() && touched.email) return 'Email is required';
    if (touched.email && !validateEmail(credentials.email)) return 'Please enter a valid email address';
    return '';
  };

  const getPasswordError = () => {
    if (!credentials.password && touched.password) return 'Password is required';
    if (touched.password && credentials.password.length < 6) return 'Password must be at least 6 characters';
    return '';
  };

  const handleFieldBlur = (field: 'email' | 'password') => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCredentials(prev => ({ ...prev, [e.target.name]: e.target.value }));
    if (errorMessage) dispatch(clearError());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    
    // Validate form fields
    if (getEmailError() || getPasswordError() || !turnstileToken || submittingRef.current) return;

    submittingRef.current = true;
    try {
      // First, login to get tokens
      const result = await dispatch(login({ ...credentials, turnstileToken })).unwrap();
      
      // Verify we have a valid user before redirecting
      if (result && result.user && result.token) {
        console.log('Login successful, checking email verification status');
        
        // Check if email verification is required
        if (!result.user.emailVerified) {
          // Email not verified - redirect to verification page
          console.log('Email not verified, redirecting to verification page');
          navigate('/verify-email', {
            state: {
              email: result.user.email || credentials.email,
              name: result.user.name || ''
            }
          });
          return;
        }
        
        console.log('Email verified, redirecting to dashboard');
        
        // Check if there's a redirect path stored
        const redirectPath = sessionStorage.getItem('redirectAfterLogin');
        if (redirectPath) {
          sessionStorage.removeItem('redirectAfterLogin');
          navigate(redirectPath);
        } else {
          // Navigate directly to the correct dashboard based on user type
          const actualUserType = result.user?.userType || result.user?.role;
          if (actualUserType === 'stylist') {
            navigate('/stylist/dashboard');
          } else if (actualUserType === 'client') {
            navigate('/client/dashboard');
          } else if (actualUserType === 'admin') {
            navigate('/admin/dashboard');
          } else {
            navigate('/admin/dashboard');
          }
        }
      } else {
        console.error('Login response missing user or token data');
      }
    } catch (err: any) {
      // Error is already handled by the rejected action in the store
      console.error('Login error:', err);
      setTurnstileToken('');
      setTurnstileReset((value) => value + 1);
    } finally {
      submittingRef.current = false;
    }
  };


  return (
    <>
    <form className="auth-form" onSubmit={handleSubmit} noValidate>
      {errorMessage && (
        <div className="auth-form__error-banner" role="alert">
          {errorMessage}
        </div>
      )}

      <TeslaInput
        id="email"
        name="email"
        type="email"
        label="Email Address"
        icon={<EmailIcon />}
        autoComplete="email"
        value={credentials.email}
        onChange={handleChange}
        onBlur={() => handleFieldBlur('email')}
        error={touched.email ? getEmailError() : ''}
        fullWidth
      />

      <TeslaInput
        id="password"
        name="password"
        type={showPassword ? 'text' : 'password'}
        label="Password"
        icon={<LockIcon />}
        actionIcon={showPassword ? <EyeOffIcon /> : <EyeIcon />}
        onActionClick={() => setShowPassword(!showPassword)}
        autoComplete="current-password"
        value={credentials.password}
        onChange={handleChange}
        onBlur={() => handleFieldBlur('password')}
        error={touched.password ? getPasswordError() : ''}
        fullWidth
      />

      <Link to="/forgot-password" className="auth-form__forgot">
        Forgot Password?
      </Link>

      <TurnstileWidget
        resetSignal={turnstileReset}
        onVerify={setTurnstileToken}
        onExpire={() => setTurnstileToken('')}
        onError={() => setTurnstileToken('')}
      />

      <TeslaButton
        type="submit"
        variant="primary"
        fullWidth
        disabled={status === 'loading' || !credentials.email || !credentials.password || !turnstileToken}
        loading={status === 'loading'}
        className="auth-form__submit"
      >
        {status === 'loading' ? 'Signing In...' : 'Sign In'}
      </TeslaButton>
    </form>
    
    <div className="auth-form__footer">
      <span>Don't have an account?</span>
      <Link 
        to="/register" 
        className="auth-form__link"
        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
          // Ensure navigation happens immediately
          e.stopPropagation();
        }}
      >
        Sign Up
      </Link>
    </div>
    </>
  );
};

export default LoginForm;